import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './index.css';

// Import console manager to control logging globally
import './utils/consoleManager';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { AffiliateAuthProvider } from './contexts/AffiliateAuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { VatProvider } from './contexts/VatContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';

// Components
import Layout from './components/Layout/Layout.jsx';
import MaterialCategories from './components/Materials/MaterialCategories.jsx';
import CategoryMaterials from './components/Materials/CategoryMaterials.jsx';
import RecipeCategories from './components/Recipes/RecipeCategories.jsx';
import CategoryRecipes from './components/Recipes/CategoryRecipes.jsx';
import FixedOverheadCosts from './components/DishCosting/FixedOverheadCosts.jsx';
import VariableOverheadCosts from './components/DishCosting/VariableOverheadCosts.jsx';
import Dashboard from './components/Dashboard/Dashboard.jsx';
import AuthContainer from './components/Auth/AuthContainer.jsx';
import EmailVerification from './components/Auth/EmailVerification.jsx';
import VerificationPending from './components/Auth/VerificationPending.jsx';
import PaymentResult from './components/Auth/PaymentResult.jsx';
import PaymentRequired from './components/Auth/PaymentRequired.jsx';
import SubscriptionExpired from './components/Auth/SubscriptionExpired.jsx';
import ForgotPassword from './components/Auth/ForgotPassword.jsx';
import ResetPassword from './components/Auth/ResetPassword.jsx';
import ProtectedRoute from './components/Auth/ProtectedRoute.jsx';
import AffiliateAuthContainer from './components/Affiliate/AffiliateAuthContainer.jsx';
import AffiliateProtectedRoute from './components/Affiliate/AffiliateProtectedRoute.jsx';
import AffiliateLayout from './components/Affiliate/AffiliateLayout.jsx';
import AffiliateDashboardPage from './components/Affiliate/AffiliateDashboardPage.jsx';
import PayoutDetailsPage from './components/Affiliate/PayoutDetailsPage.jsx';
import AffiliateReferralLinkPage from './components/Affiliate/AffiliateReferralLinkPage.jsx';
import AdminAuthContainer from './components/Admin/AdminAuthContainer.jsx';
import AdminProtectedRoute from './components/Admin/AdminProtectedRoute.jsx';
import AdminLayout from './components/Admin/AdminLayout.jsx';
import AdminDashboardPage from './components/Admin/Dashboard/AdminDashboardPage.jsx';
import PromoCodesPage from './components/Admin/PromoCodes/PromoCodesPage.jsx';
import SaleCampaignsPage from './components/Admin/SaleCampaigns/SaleCampaignsPage.jsx';
import ExistingAffiliatesPage from './components/Admin/Affiliates/ExistingAffiliatesPage.jsx';
import NewAffiliateRequestsPage from './components/Admin/Affiliates/NewAffiliateRequestsPage.jsx';
import ExistingUsersPage from './components/Admin/Users/ExistingUsersPage.jsx';
import PricingListPage from './components/Pricing/PricingListPage.jsx';
import ApplicationsPricing from './components/Pricing/ApplicationsPricing.jsx';

// Utils
import { initializeDefaultData, getLanguage, saveLanguage } from './utils/storage';
import './utils/authUtils.js'; // For testing - adds clearAuthData to window

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Initialize default data
    initializeDefaultData();
    
    // Set language from storage
    const savedLanguage = getLanguage();
    if (savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    saveLanguage(language);
  };

  return (
    <ThemeProvider>
      <AuthProvider>
        <AffiliateAuthProvider>
        <AdminAuthProvider>
          <VatProvider>
            <CurrencyProvider>
              <Router>
              <div className="App">
                <Routes>
                  {/* Regular User Routes */}
                  <Route path="/login" element={<AuthContainer />} />
                  <Route path="/register" element={<AuthContainer />} />
                  <Route path="/verify-email" element={<EmailVerification />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verification-pending" element={<VerificationPending />} />
                  <Route path="/payment/result" element={<PaymentResult />} />
                  <Route path="/payment-required" element={<PaymentRequired />} />
                  <Route path="/subscription-expired" element={<SubscriptionExpired />} />
                  
                  {/* Affiliate Routes */}
                  <Route path="/affiliate/login" element={<AffiliateAuthContainer />} />
                  <Route path="/affiliate/register" element={<AffiliateAuthContainer />} />
                  <Route path="/affiliate/verify-email" element={<EmailVerification />} />
                  <Route path="/affiliate/forgot-password" element={<ForgotPassword />} />
                  <Route path="/affiliate/reset-password" element={<ResetPassword />} />
                  <Route path="/affiliate/verification-pending" element={<VerificationPending />} />
                  <Route path="/affiliate" element={
                    <AffiliateProtectedRoute>
                      <AffiliateLayout />
                    </AffiliateProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/affiliate/dashboard" replace />} />
                    <Route path="dashboard" element={<AffiliateDashboardPage />} />
                    <Route path="referral-link" element={<AffiliateReferralLinkPage />} />
                    <Route path="payout-details" element={<PayoutDetailsPage />} />
                  </Route>
                  <Route path="/admin/login" element={<AdminAuthContainer />} />
                  <Route
                    path="/admin"
                    element={
                      <AdminProtectedRoute>
                        <AdminLayout />
                      </AdminProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboardPage />} />
                    <Route path="affiliate-managment/existing-affiliates" element={<ExistingAffiliatesPage />} />
                    <Route path="affiliate-managment/new-requests" element={<NewAffiliateRequestsPage />} />
                    <Route path="affiliates" element={<Navigate to="/admin/affiliate-managment/existing-affiliates" replace />} />
                    <Route path="user-managment" element={<ExistingUsersPage />} />
                    <Route path="user-managment/existing-users" element={<Navigate to="/admin/user-managment" replace />} />
                    <Route path="promo-codes" element={<PromoCodesPage />} />
                    <Route path="sale-campaigns" element={<SaleCampaignsPage />} />
                  </Route>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/materials" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <MaterialCategories />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/materials/category/:categoryId" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <CategoryMaterials />
                    </Layout>
                  </ProtectedRoute>
                } />
                        {/* Dish Costing */}
            <Route path="/dish-costing/recipes" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <RecipeCategories />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/dish-costing/recipes/category/:categoryId" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <CategoryRecipes />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/dish-costing/fixed-overhead" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <FixedOverheadCosts />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/dish-costing/variable-overhead" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <VariableOverheadCosts />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Pricing */}
                <Route path="/pricing" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <PricingListPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Applications Pricing */}
                <Route path="/applications-pricing" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <ApplicationsPricing />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Legacy redirects */}
                        <Route path="/recipes" element={<Navigate to="/dish-costing/recipes" replace />} />
            <Route path="/recipes/category/:categoryId" element={<Navigate to="/dish-costing/recipes/category/:categoryId" replace />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout onLanguageChange={handleLanguageChange}>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
              </Routes>
            </div>
              </Router>
            </CurrencyProvider>
          </VatProvider>
        </AdminAuthProvider>
        </AffiliateAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 